import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useProntoContext } from "../../../providers/pronto"
import Money from "../../../../utils/money";
import { kebabCase } from "../../../../utils/kebabCase";
import { useIsMobile } from "../../../hooks/useIsMobile";
import {
  ResultCardProductProps,
} from "@usereactify/search";
import { Badges } from '../Badges/product-badges';

// export type ProductCardProps = Props & React.ComponentProps<
//   NonNullable<ResultCardProductProps["render"]>
// >;

type ProductCardProps = {
  props: React.ComponentProps<
    NonNullable<ResultCardProductProps["render"]>
  >;
  promoItem?: PromoItem;
};

type Swatch = {
  handle: string;
  hexCode: string;
  name: string;
  stockCode: string;
  swatchId: string;
  url: string;
};

type PromoItem = {
  sku: string;
  price: number;
  markdown_price: number;
  elements: [
    {
      markdown: boolean;
      code: string;
      description: string;
      members_only: boolean;
      campaign: {
        code: string;
        title: string;
      };
    }
  ];
};
const ProductCard: React.FC<ProductCardProps> = ({ props, promoItem }) => {
  const { product } = props

  const [swatches, setSwatches] = useState<any>([]);
  const isSale: boolean = product?.variants[0]?.price < product.variants[0]?.compare_at_price;
  const isClearance: boolean =
    !!product?.collections?.filter(({ handle }) => handle === "clearance").length ||
    !!product?.tags?.filter((tag) => tag.includes("clearance")).length;

  const secondaryImage = product?.images.length > 1 ? product.images[product.images.length - 1]?.src : product?.images[0]?.src;
  const isSoldOut = product?.variants?.some(item => {
    return item?.available == false && item.inventory_policy === "DENY"
  });

  useEffect(() => {
    const parseSwatches = () => {
      // First check for swatch tags
      const swatchTags = product.tags?.filter(tag => tag.startsWith('swatch:')) || [];
      const FILE_PREFIX = 'https://cdn.shopify.com/s/files/1/0594/7444/4476/files/';
      
      if (swatchTags.length) {
        const parsedSwatches = swatchTags.map(tag => {
          const [, handle, name, value] = tag.split(':');
          return {
            handle,
            name: name.replace(/-/g, ' '),
            ...(value?.startsWith('#')
              ? { hexCode: value }
              : { url: `${FILE_PREFIX}${value}` })
            };
        });
        setSwatches(parsedSwatches);
      } else {
        // Fallback to metafield siblings
        const prontoSiblings = product.metafields?.filter(({ key }) => key === "siblings")[0]?.value;
        const siblings = prontoSiblings ? JSON.parse(prontoSiblings) : null;
        const metafieldSwatches = siblings?.swatches;
        metafieldSwatches?.length && setSwatches(metafieldSwatches || []);
      }
    }
    parseSwatches()
  }, [product])

  const hasMarkdownPricing = useMemo(() => !!promoItem?.markdown_price && promoItem?.price !== promoItem?.markdown_price, [promoItem])
  const hasMembersPricing = useMemo(() => promoItem?.elements.find((ele) => ele.members_only), [promoItem])

  return (
    <div className="product-card text-center">
      <button
        className={`product_${product.id} add_to_wishlist swym-button swym-add-to-wishlist-view-product`}
        data-swaction="addToWishlist"
        data-with-epi="true"
        data-product-url={`https://saltandpepper.com.au/products/${product.handle}`}
        data-product-id={product?.id}
        data-variant-id={product?.variants[0]?.id}
      >
        <svg width="24" height="20" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M20.8807 2.59133C20.3676 2.08683 19.7583 1.68663 19.0878 1.41358C18.4172 1.14054 17.6985 1 16.9727 1C16.2468 1 15.5281 1.14054 14.8576 1.41358C14.187 1.68663 13.5778 2.08683 13.0646 2.59133L11.9997 3.63785L10.9348 2.59133C9.89834 1.57276 8.49258 1.00053 7.02679 1.00053C5.56099 1.00053 4.15523 1.57276 3.11876 2.59133C2.08229 3.6099 1.5 4.99139 1.5 6.43187C1.5 7.87235 2.08229 9.25383 3.11876 10.2724L4.18367 11.3189L11.9997 19L19.8158 11.3189L20.8807 10.2724C21.3941 9.76814 21.8013 9.16942 22.0791 8.51045C22.357 7.85148 22.5 7.14517 22.5 6.43187C22.5 5.71857 22.357 5.01225 22.0791 4.35328C21.8013 3.69431 21.3941 3.09559 20.8807 2.59133Z"
            stroke="#8A8C8B"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <figure className="product-featured-image thb-hover">
        <a
          href={product.url}
          title={product.title}
          className="product-featured-image-link aspect-ratio aspect-ratio--portrait"
        >
          {product?.images && (
            <img
              className="lazyautosizes ls-is-cached lazyloaded product-primary-image"
              width="291"
              height="291"
              data-sizes="auto"
              src={`${product.images[0]?.src}&width=357`}
              data-srcset={product.images[0]?.src}
              alt={product.title}
              sizes="100px"
              loading="lazy"
            />
          )}
          {secondaryImage && (
            <img
              className="lazyautosizes ls-is-cached lazyloaded product-secondary-image"
              width="291"
              height="291"
              data-sizes="auto"
              src={`${secondaryImage}&width=357`}
              data-srcset={secondaryImage}
              alt={product.title}
              sizes="100px"
              loading="lazy"
            />
          )}
        </a>
      </figure>
      <div className="product-card-info">
        <div className="product-card-vendor">
          <a href={product.url} title={product.title} className="cart-product-link fs-large fw-bold">
            {product.title}
          </a>
          {
            hasMarkdownPricing && hasMembersPricing ? (
              <span className="price fs-medium">
                <ins>
                  <span className="amount mint-dark">
                    Members: <Money price={(promoItem.markdown_price / 100).toFixed(2)} />
                  </span>
                </ins>
              </span>
            ) : hasMarkdownPricing && !hasMembersPricing ? (
              <span className="price">
                <del>
                  <span className="amount"><Money price={(promoItem.price / 100).toFixed(2)}/></span>
                </del>
                <ins>
                  <span className="amount">
                    <Money price={(promoItem.markdown_price / 100).toFixed(2)} />
                  </span>
                </ins>
              </span>
            ) : (
              <span className="price fs-medium">
                {isSale && (
                  <del>
                    <span className="amount"><Money price={product.variants[0]?.compare_at_price.toFixed(2)}/></span>
                  </del>
                )}
                <ins>
                  <span className="amount">
                    <Money price={product.price_min} />
                  </span>
                </ins>
              </span>
            )
          }
          {swatches && <Swatches swatches={swatches} product={product} />}
        </div>
        <div className="collection-product-badges-wrapper">
          <div className="collection-product-badges">
            {
              promoItem?.elements.length
                ? promoItem?.elements?.map((element) => (
                  <span className={`promo-badge mint promo-${kebabCase(element.description)}`}>
                    {element.description}
                  </span>
                ))
                : isSale ? isClearance ? <span className="badge onsale promo-clearance">CLEARANCE</span> : <span className="badge onsale promo-sale">SALE</span> : null
            }
            {product?.tags && <Badges badges={product?.tags} />}
            {isSoldOut && <span className="badge out-of-stock">Sold Out</span>}
          </div>
        </div>
      </div>
    </div>
  )
};

const MemoProductCard = memo(ProductCard)
export { MemoProductCard as ProductCard }

const InstantSearchCard: React.FC<ProductCardProps> = (props) => {
  const [promoItem, setPromoItem] = useState<PromoItem | null>(null);
  const { product } = props

  // promotion engine - calculate promo items
  useEffect(() => {
    let baseUrl = "https://saltandpepper.com.au/apps/connector";
    if (Shopify?.shop.includes('staging')) {
      baseUrl = "https://salt-and-pepper-au-staging.myshopify.com/apps/connector";
    }
    const fetchPromoItem = async () => {
      const sku = product.variant_skus?.[0];
      const price = Number((Number(product.price_min) * 100).toFixed(0));

      const body = {
        items: [
          {
            sku,
            price,
          },
        ],
      };

      const response = await fetch(`${baseUrl}/api-promotionMarkdown`, {
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const { data } = await response.json();
      const promoItem = data?.items?.[0];

      if (promoItem) {
        setPromoItem(promoItem);
      }
    };

    fetchPromoItem();
  }, []);

  const hasMarkdownPricing = useMemo(() => !!promoItem?.markdown_price && promoItem?.price !== promoItem?.markdown_price, [promoItem])
  const hasMembersPricing = useMemo(() => promoItem?.elements.find((ele) => ele.members_only), [promoItem])

  return (
    <div className="product-cart-item fs-large">
      <a href={product.url}>
        <div className="product-cart-item-image">
          {product?.image && (
            <img
              className="lazyautosizes ls-is-cached lazyloaded"
              width="107"
              height="107"
              data-sizes="auto"
              src={product?.image}
              data-srcset={product.image}
              alt={product.title}
              sizes="100px"
              loading="lazy"
            />
          )}
        </div>
      </a>
      <div>
        <a href={product.url} title={product.title} className="cart-product-link">
          {product.title}
        </a>

        {hasMarkdownPricing && !hasMembersPricing ? (
          <span className="price">
            <del>
              <span className="amount">${(promoItem.price / 100).toFixed(2)}</span>
            </del>
            <ins>
              <span className="amount">
                <Money price={(promoItem.markdown_price / 100).toFixed(2)} />
              </span>
            </ins>
          </span>
        ) : (
          <span className="price">
            <span className="amount">
              <Money price={product.price_min} />
            </span>
          </span>
        )}
        {hasMarkdownPricing && hasMembersPricing && (
          <span className="price fs-medium">
            <ins>
              <span className="amount mint-dark">
                <Money price={(promoItem.markdown_price / 100).toFixed(2)} />
              </span>
            </ins>
          </span>
        )}
      </div>
    </div>
  )
};

const MemoInstantSearchCard = memo(InstantSearchCard)
export { MemoInstantSearchCard as InstantSearchCard }

const Swatches = ({ swatches, product }): { swatches: Swatch[]; product: any } => {
  if (!swatches) return;
  const isMobile = useIsMobile();

  const maxSwatches = isMobile ? 5 : 7;
  const remainingSwatches = swatches.length - maxSwatches;

  return (
    <div className="product-card-swatches">
      {swatches.splice(0, maxSwatches)?.map(({ stockCode, handle, hexCode, url }) => {
        if (stockCode === product.variants[0].sku) return;

        return (
          <a href={`/products/${handle}`}>
            <div
              className="product-card-swatch"
              style={{ backgroundColor: hexCode, backgroundImage: `url(${url})` }}
            ></div>
          </a>
        );
      })}
      {remainingSwatches > 0 && <div className="product-card-swatch-more">+{remainingSwatches}</div>}
    </div>
  );
};

const MemoSwatches = memo(Swatches)
export { MemoSwatches as Swatches }